"use client";

import { Info } from "lucide-react";

import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaPatientInfoHeader } from "@/src/components/tasks/athena-patient-info-header";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { ReferringProviderSection } from "@/src/components/tasks/referring-provider-section";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { formatDateMmDdYyyy } from "@/src/lib/utils/date-and-phone-utils";
import { PriorAuthorizationFields } from "@/src/models/blocks/block";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Referral } from "@/src/models/documents/referral";

function getStridecareCaseSpecialtyLabel(serviceLine: ServiceLine | null): string {
  if (serviceLine === ServiceLine.PODIATRY) return "podiatry";
  if (serviceLine === ServiceLine.FIBROIDS) return "fibroids";

  return "vascular";
}

export interface StridecareAthenaData extends AthenaReferralCardsData {
  subject: string | null;
  note: string | null;
  caseAssignee: string | null;
  actionNote: string | null;
  resolvedProvider: string | null;
  patientPrimaryProvider: string | null;
  primaryServiceLine: ServiceLine | null;
  patientPrimaryDepartmentName: string | null;
  resolvedDepartmentName: string | null;
}

interface StridecareReferralInstructionsProps {
  referral: Referral;
  athenaData: StridecareAthenaData | null;
  error?: string;
  taskId: string;
  priorAuthFields?: PriorAuthorizationFields | null;
  willCreateCase?: boolean;
}

export function StridecareReferralInstructions({
  referral,
  athenaData,
  error,
  taskId,
  priorAuthFields,
  willCreateCase,
}: StridecareReferralInstructionsProps) {
  return (
    <div className="space-y-8 text-foreground">
      {(error || !athenaData) && <ErrorAlert compact={true} message={error || "No Athena data available"} />}

      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <AthenaPatientInfoHeader
            title="Stridecare Referral Instructions"
            patientDetails={athenaData?.patientDetails || null}
            document={athenaData?.document || null}
          />

          <ReferringProviderSection referral={referral} title="Referring Information:" />

          {willCreateCase && (
            <div className="rounded-lg border border-blue-500/50 bg-blue-500/10 p-4 mb-4">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Please verify a {getStridecareCaseSpecialtyLabel(referral.serviceLine ?? null)} patient case exists in
                  Athena for this referral. A case from a different specialty does not count — only a matching specialty
                  case is sufficient.
                </p>
              </div>
            </div>
          )}

          <div className="mb-4 my-2">
            <h3 className="font-semibold text-foreground my-2">Case Information:</h3>
            <div className="grid grid-cols-3">
              <div>
                <span className="font-medium">Case Subject: </span>
                <CopyableContent text={athenaData?.subject || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Valerie Determined Provider: </span>
                <CopyableContent text={athenaData?.resolvedProvider || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Patient Primary Provider: </span>
                <CopyableContent text={athenaData?.patientPrimaryProvider || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Valerie Determined Department: </span>
                <CopyableContent text={athenaData?.resolvedDepartmentName || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Patient Primary Dept: </span>
                <CopyableContent text={athenaData?.patientPrimaryDepartmentName || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Case Assignee: </span>
                <CopyableContent text={athenaData?.caseAssignee || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Referral Service Line: </span>
                <CopyableContent text={referral.serviceLine || "N/A"} />
              </div>
              <div>
                <span className="font-medium">Primary Service Line: </span>
                <CopyableContent text={athenaData?.primaryServiceLine || "N/A"} />
              </div>
            </div>
            <div className="mt-2">
              <span className="font-medium">Case Description: </span>
              <CopyableContent text={athenaData?.note || "N/A"} />
            </div>
            <div className="mt-2">
              <span className="font-medium">Action Note: </span>
              <CopyableContent text={athenaData?.actionNote || "N/A"} />
            </div>
          </div>

          {priorAuthFields && (
            <div className="mb-4 my-2">
              <h3 className="font-semibold text-foreground my-2">Authorization:</h3>
              <div className="grid grid-cols-3">
                <div>
                  <span className="font-medium">Auth Number: </span>
                  <CopyableContent text={priorAuthFields?.requestId || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">Auth Type: </span>
                  <CopyableContent text={priorAuthFields?.authorizationType || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">Auth Status: </span>
                  <CopyableContent text={priorAuthFields?.authorizationStatus || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">Auth For: </span>
                  <CopyableContent text={priorAuthFields?.authorizationFor || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">Number of Services: </span>
                  <CopyableContent text={priorAuthFields?.numberOfServices?.toString() || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">Start Date: </span>
                  <CopyableContent text={formatDateMmDdYyyy(priorAuthFields?.startDate) || "N/A"} />
                </div>
                <div>
                  <span className="font-medium">End Date: </span>
                  <CopyableContent text={formatDateMmDdYyyy(priorAuthFields?.endDate) || "N/A"} />
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <SyncIncompleteActions taskId={taskId} />
          </div>
        </div>
      </EhrInstructionsCard>

      <ReferralEligibilityCard referralId={referral.id} taskId={taskId} />

      <AthenaReferralCards referral={referral} athenaData={athenaData} />
    </div>
  );
}
